// Local feature switches — flip in code + rebuild (no backend / appConfig row).

/**
 * "NewMyTripsCancel & Modify"
 *
 * Flip to "new" to route MyTrips flight Cancel + Modify through the new endpoints
 * (flight/cancel/request, flight/modify/select-sectors). "old" keeps the legacy
 * trips/flights/cancel + trips/flights/change endpoints.
 *
 * One-line dev switch — change this value and rebuild to toggle the whole flow.
 */
export const NEW_MY_TRIPS_CANCEL_AND_MODIFY: "new" | "old" = "new";

/** True when the new MyTrips cancel/modify endpoints should be used. */
// `as string` is required: NEW_MY_TRIPS_CANCEL_AND_MODIFY is a const, so TS narrows
// its flow type to the assigned literal ("old") and would flag `=== "new"` as a
// no-overlap comparison (TS2367). Widening to string keeps the toggle valid. Do NOT
// "simplify" this back to a bare `=== "new"`.
export const isNewMyTripsCancelAndModify = (): boolean => (NEW_MY_TRIPS_CANCEL_AND_MODIFY as string) === "new";

/**
 * "Facebook login"
 *
 * Flip to "on" to render the "Continue with Facebook" button on all 4 social
 * surfaces (3 signup + 1 login). "off" hides every Facebook button while
 * leaving all FB SDK init + handlers in place — flipping back on requires no
 * other change. See CLAUDE.md → "Facebook Login (Login + Signup)".
 */
export const FACEBOOK_LOGIN: "on" | "off" = "off";

/** True when the Facebook login/signup buttons should be visible. */
// Same `as string` widening reason as isNewMyTripsCancelAndModify above — keeps
// TS from narrowing the const literal and flagging `=== "on"` as no-overlap.
export const isFacebookLoginEnabled = (): boolean => (FACEBOOK_LOGIN as string) === "on";

/**
 * "Hotel details-by-unicaId enrichment call"
 *
 * Flip to "off" to skip POST /search/hotels/details-by-unicaid on hotel-card
 * expand. The expanded card then renders directly from the search-result
 * `hotelDetails` (existing fallback path) — no skeleton, no network request.
 * "on" keeps the current behaviour (fetch richer images/amenities/about,
 * fall back to search data on any failure).
 *
 * Single gate point: the only caller is `useHotelCardLogic.tsx` (the `if`
 * around `getHotelDetailsByUnicaId`). Gating there skips both the request AND
 * the `setIsFetchingHotelDetailsByUnicaId(true)` skeleton flip.
 */
export const HOTEL_DETAILS_BY_UNICA_ID: "on" | "off" = "off";

/** True when the details-by-unicaId enrichment call should fire on card expand. */
// Same `as string` widening reason as the switches above — keeps TS from
// narrowing the const literal and flagging `=== "on"` as no-overlap (TS2367).
export const isHotelDetailsByUnicaIdEnabled = (): boolean =>
  (HOTEL_DETAILS_BY_UNICA_ID as string) === "on";

/**
 * "Allow hyphen + apostrophe in traveler names"
 *
 * Flip to "on" to let the traveler-form First/Middle/Last name inputs accept
 * `-` and `'` (e.g. "Jean-Marie", "O'Brien"). "off" (default) restricts names
 * to letters + digits + dot + space only (no hyphen/apostrophe). Consumed by
 * `isValidNameInput` in `utils/booking/travelerForm/validateForm.ts`, so it
 * applies uniformly to typing + the block/on-blur name validation.
 */
export const ALLOW_HYPHEN_APOS: "on" | "off" = "off";

/** True when `-` and `'` are allowed in traveler names. */
// Same `as string` widening reason as the switches above — keeps TS from
// narrowing the const literal and flagging `=== "on"` as no-overlap (TS2367).
export const isAllowHyphenAposEnabled = (): boolean => (ALLOW_HYPHEN_APOS as string) === "on";

/**
 * "Allow number + dot in traveler names"
 *
 * Flip to "on" to let the traveler-form First/Middle/Last name inputs accept
 * digits `0-9` and dot `.`. "off" (default) restricts names to letters + spaces
 * only (no digits, no dot) — plus `-`/`'` when ALLOW_HYPHEN_APOS is on. Consumed
 * by `isValidNameInput` in `utils/booking/travelerForm/validateForm.ts`, so it
 * applies uniformly to typing + the block/on-blur name validation.
 */
export const ALLOW_NUM_DOT: "on" | "off" = "off";

/** True when digits `0-9` and dot `.` are allowed in traveler names. */
// Same `as string` widening reason as the switches above — keeps TS from
// narrowing the const literal and flagging `=== "on"` as no-overlap (TS2367).
export const isAllowNumDotEnabled = (): boolean => (ALLOW_NUM_DOT as string) === "on";

/**
 * "Show the Extra Info accordion on room-price rows"
 *
 * ⚠️ NO LONGER THE SWITCH. This feature moved to the appConfig row
 * `SHOW_ROOM_EXTRA_INFO`, and this constant is now only the fallback used when
 * that row does NOT exist (and on the first render, before the config fetch
 * resolves). Flipping it still changes behaviour in any environment with no
 * row, so it is not dead — but the row wins wherever one is present.
 *
 * To turn the feature off, add the row with a falsy value. Do not flip this to
 * `false` for that: it would also switch the feature off in every environment
 * that has no row, which is the opposite of a targeted rollback.
 *
 * Renders the "Extra Info" pill below the Refundable / Breakfast row inside
 * every RoomRateItem. The pill appears when the rate has any extraInfo section;
 * clicking expands the content below the row (accordion — one open at a time
 * across the whole modal; same pill re-click collapses).
 *
 * Gate:     src/helpers/roomExtraInfo.ts (isRoomExtraInfoEnabled)
 * Consumer: src/component/reusableComponent/roomWithImages/components/RoomRateItem.tsx
 */
export const SHOW_NOTES: boolean = true;


/**
 * "Hotel filter — old Star Rating card design"
 *
 * Flip to `true` to render the Hotel Filter's Star Rating section as a 5-card
 * grid (1..5 each with a purple `<StarRoundedIcon />`), matching the Booking
 * Preferences modal's Star Ratings block. `false` = the current PillMulti text
 * chips (`"5 stars (12)"`, etc.).
 *
 * Selection state (`sel.stars`) is unchanged in both modes — only the render
 * differs. Consumer: cityFilterControls.tsx `case "stars"` branch.
 */
export const STAR_DESIGN_OLD: boolean = true;

/** True when the Hotel Filter's Star Rating section should render as star cards. */
export const isStarDesignOldEnabled = (): boolean => STAR_DESIGN_OLD;


/**
 * "Flight filter — Departure & Arrival time sliders vs daypart pill grid"
 *
 * Flip to "on" to keep the existing "Departs" / "Arrives" dual-range sliders
 * inside the flight filter's "Departure & arrival times" section (renders
 * exactly as today). "off" (default) swaps in a 2-column icon-pill grid
 * (Early morning / Morning / Afternoon / Evening / Night — two grids, one
 * under "Take-off", one under "Landing"), matching flight-filter-mockup.html.
 *
 * Payload shape:
 *  • Flag ON  → `departureTime` / `arrivalTime` are `{min,max}` MINUTE ranges,
 *    emitted only when narrowed (current slider behaviour).
 *  • Flag OFF → `departureTime` / `arrivalTime` are string arrays of daypart
 *    keys (e.g. ["morning","afternoon"]), emitted only when at least one pill
 *    is picked. Empty selection → key omitted.
 * The two shapes are mutually exclusive per request; `iSelectedFilterLeg` types
 * both as `{min,max} | string[]`. Backend must accept whichever the flag emits.
 *
 * Consumers: legFilterControls.tsx (renderFamily "times", chipActive,
 * famSelCount, appliedCount); mapSelectionToSelectedFilters.ts (emission).
 */
export const DEPARTURE_ARRIVAL_SLIDER: "on" | "off" = "off";


export const isDepartureArrivalSliderEnabled = (): boolean =>
  (DEPARTURE_ARRIVAL_SLIDER as string) === "on";

/**
 * "Multi-City hotel — last-result 'adjust your filters' CTA"
 *
 * Flip to "on" to render the "Not seeing what you are looking for? Tap here to
 * adjust your filters." card at the bottom of the currently-selected city's
 * hotel list inside the multi-city hotel results (DisplayPriceDetails multi-city
 * branch). "off" hides the card in multi-city (single-city still renders it —
 * that gate is `!loadMoreHotels && noMoreHotels`, unaffected).
 *
 * Only takes effect when SHOW_PREF is on (i.e. the new filter bar is showing);
 * with SHOW_PREF off the legacy "adjust your preferences" CTA renders in the
 * single-city branch only, same as today.
 */
export const MULTI_CITY_LAST_FILTER: "on" | "off" = "off";


export const isMultiCityLastFilterEnabled = (): boolean =>
  (MULTI_CITY_LAST_FILTER as string) === "on";

/**
 * "Flight filter — dynamic Departure & Arrival daypart buckets"
 *
 * Flip to "on" (default) to render only the daypart chips (Early morning /
 * Morning / Afternoon / Evening / Night) that appear in the API's
 * filtersAvailable.<leg>.departureTime.buckets / arrivalTime.buckets. "off"
 * keeps the pre-existing behavior of always rendering all 5 chips from the
 * hardcoded FLIGHT_TIME_PARTS constant.
 *
 * Only affects the pill-grid variant. When DEPARTURE_ARRIVAL_SLIDER is on
 * (dual-range sliders) this flag has no effect — sliders read {min,max}, not
 * buckets.
 *
 * Selection keys, payload emission (mapSelectionToSelectedFilters.ts), and
 * chip icon/label/range look-ups (FLIGHT_TIME_PARTS) are unchanged. Only the
 * per-leg render list is filtered.
 *
 * Consumers: legFilterControls.tsx (TimeChipGrid, mergeFacets, renderFamily
 * "times"); mapFiltersAvailableToTripFixture.ts (bucket pass-through).
 */
export const DEP_ARR_DYNAMIC: "on" | "off" = "on";

// Same `as string` widening reason as the switches above — keeps TS from
// narrowing the const literal and flagging `=== "on"` as no-overlap (TS2367).
export const isDepArrDynamicEnabled = (): boolean =>
  (DEP_ARR_DYNAMIC as string) === "on";



// export const DROP_NULL_AIRPORTS: boolean = false;

// export const FILTER_DROPDOWN_ACTIVE = true;

// export const ENABLE_JOI_VALIDATION: "on" | "off" = "on";

// export const BUILD_VERSION = "2026.07.07";

// export const ACTIVATE_BUILD_CLEANUP = true;

// export const SHOW_NEIGHBORHOOD: boolean = false;

// export const HIDE_BLANK_FILTER_SECTION: boolean = true;


